/**
 * Keyboard shortcuts for the player, as a pure key -> action decision.
 *
 * The window-level listener that feeds this is three lines of glue; every rule
 * about when a keypress is NOT ours lives here, where it can be tested. Those
 * rules matter more than the mappings: the player page also hosts a Q&A
 * textarea, a notes editor, a sidebar of lesson buttons and a Mark-complete
 * button, and a global handler that ignored them would let one Space both
 * activate the focused button and pause the video.
 */
export type PlayerAction =
  | { type: 'toggle' }
  | { type: 'seekBy'; seconds: number }
  | { type: 'seekPct'; pct: number }
  | { type: 'volumeBy'; delta: number }
  | { type: 'mute' }
  | { type: 'fullscreen' }
  | { type: 'captions' }
  | { type: 'rateStep'; dir: 1 | -1 }
  | { type: 'bookmark' };

export type KeyInput = {
  key: string;
  shiftKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  /** Auto-repeat from a held key — never actioned, or a hold becomes a storm. */
  repeat?: boolean;
  targetTag?: string;
  targetRole?: string | null;
  targetEditable?: boolean;
  /** Focus is inside the player wrapper. */
  insidePlayer?: boolean;
  dialogOpen?: boolean;
  kind: 'video' | 'iframe';
};

const TEXT_ENTRY = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
/** Elements that own Space/Enter/arrows themselves. */
const ACTIVATABLE_TAGS = new Set(['BUTTON', 'A', 'SUMMARY']);
const ACTIVATABLE_ROLES = new Set(['button', 'tab', 'menuitem', 'link', 'checkbox', 'radio']);

export const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

/** Step to the next supported rate in `dir`, clamping at both ends. */
export function nextRate(current: number, dir: 1 | -1, rates: number[] = PLAYBACK_RATES): number {
  const ordered = [...rates].sort((a, b) => a - b);
  if (ordered.length === 0) return current;
  const index = ordered.findIndex((r) => Math.abs(r - current) < 0.001);
  if (index === -1) return ordered[0] as number;
  const next = index + dir;
  if (next < 0) return ordered[0] as number;
  if (next >= ordered.length) return ordered[ordered.length - 1] as number;
  return ordered[next] as number;
}

export function resolvePlayerKeyAction(input: KeyInput): PlayerAction | null {
  if (input.repeat) return null;
  if (input.ctrlKey || input.metaKey || input.altKey) return null;
  if (input.dialogOpen) return null;
  if (input.targetEditable) return null;
  const tag = (input.targetTag ?? '').toUpperCase();
  if (TEXT_ENTRY.has(tag)) return null;

  const key = input.key;
  const activatable =
    ACTIVATABLE_TAGS.has(tag) ||
    (input.targetRole != null && ACTIVATABLE_ROLES.has(input.targetRole));
  const arrow =
    key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown';

  // Space/Enter/arrows belong to whatever control has focus. Claiming them would
  // make one keypress do two things (toggle a lesson AND pause the video).
  if ((key === ' ' || key === 'Spacebar' || key === 'Enter' || arrow) && activatable) return null;

  switch (key) {
    case ' ':
    case 'Spacebar':
      // Only from the page body or from inside the player itself.
      if (!input.insidePlayer && tag !== 'BODY' && tag !== '') return null;
      return { type: 'toggle' };
    case 'k':
    case 'K':
      return { type: 'toggle' };
    case 'j':
    case 'J':
      return { type: 'seekBy', seconds: -10 };
    case 'l':
    case 'L':
      return { type: 'seekBy', seconds: 10 };
    case 'ArrowLeft':
      return { type: 'seekBy', seconds: -5 };
    case 'ArrowRight':
      return { type: 'seekBy', seconds: 5 };
    case 'ArrowUp':
    case 'ArrowDown':
      // Volume is native-only (a provider iframe owns its own), and only when
      // the learner is actually in the player — otherwise it hijacks scrolling.
      if (input.kind !== 'video' || !input.insidePlayer) return null;
      return { type: 'volumeBy', delta: key === 'ArrowUp' ? 0.1 : -0.1 };
    case 'm':
    case 'M':
      return { type: 'mute' };
    case 'f':
    case 'F':
      return { type: 'fullscreen' };
    case 'c':
    case 'C':
      return { type: 'captions' };
    case 'b':
    case 'B':
      return { type: 'bookmark' };
    case '>':
    case '.':
      return input.shiftKey ? { type: 'rateStep', dir: 1 } : null;
    case '<':
    case ',':
      return input.shiftKey ? { type: 'rateStep', dir: -1 } : null;
    default:
      if (/^[0-9]$/.test(key)) return { type: 'seekPct', pct: Number(key) * 10 };
      return null;
  }
}
